/**
 * AuthSurface — the passkey assertion ceremony (pre-shell gate). Uses @simplewebauthn/browser
 * against the Nest WebAuthn guard (spec-compliant). No app surface is reachable until a session is
 * established (INTEGRATION.md §6). On success it stores the CSRF token for subsequent mutations.
 *
 * This is the FALLBACK path only: App first tries `restoreSession()` (lib/session), so an operator
 * with a live cookie never sees this surface.
 *
 * DESIGN — this is the product's front door and the only page a non-operator can reach. It is a
 * hero, but the hero of a single-operator infrastructure console: an instrument, not a campaign.
 * The composition (full-bleed drawn field, copy high and left, light display type, one hard bottom
 * edge) is adapted from the reference named for this work; the marketing content is not. The
 * layout rationale lives beside the `.hero*` rules in styles/components.css.
 *
 * COPY IS LOAD-BEARING HERE (rubric R8 — the interface must not lie). Every claim on this surface
 * was checked against the code that enforces it before it was written:
 *   · "a single enrolled passkey" / "a second enrolment is refused" — DurableCredentialStore.save()
 *     throws SingleOperatorViolation once the store is ENROLLED, and treats CORRUPT as occupied.
 *   · "no password is ever sent" — the only credential path is the WebAuthn assertion below.
 *   · "no way to enrol one from this screen" — true, and stated OUT LOUD rather than left as an
 *     unexplained absence, because the missing "register another device" link is the single most
 *     likely thing for an operator to go looking for here.
 * The previous copy ("One console. Every system.") was a claim the page could not stand behind,
 * and the mono tag strip along the bottom read as a status bar reporting live state, which it
 * never was.
 */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { z } from 'zod';
import { apiGet, apiPost, setCsrfToken, ApiRefusal } from '../../lib/api';
import { sessionSchema } from '../../lib/session';

const optionsSchema = z.object({ options: z.record(z.unknown()) });

/**
 * The three properties that actually characterise this surface. Static facts about the deployment,
 * written as term + gloss so none of them needs source code to interpret (R5) — and rendered on a
 * neutral ground with no dots or fills, so nothing here can be misread as a live indicator (R1).
 */
const RAILS: ReadonlyArray<{ term: string; gloss: string }> = [
  {
    term: 'Loopback-only',
    gloss: 'The service binds 127.0.0.1. Reachability is a named tunnel, never an open port.',
  },
  {
    term: 'WebAuthn',
    gloss: 'A hardware-backed assertion from your own device. No password is sent, or stored.',
  },
  {
    term: 'Single-operator',
    gloss: 'The credential store holds exactly one passkey. A second enrolment is refused.',
  },
];

/**
 * The shared frame. Both the sign-in state and the session-restoring state render THROUGH this, so
 * restoring reads as the same page settling rather than a different screen flashing past — and,
 * less cosmetically, so both states carry the same single `<h1>`. The restoring frame previously
 * had no heading at all, which meant the document outline was empty on first paint (R4).
 */
export function HeroFrame({ action }: { action: ReactNode }) {
  return (
    <main className="hero">
      <header className="hero__nav">
        <div className="hero__brand">
          QuorumWorks <span>operator portal</span>
        </div>
        {/* Read from the browser, so it cannot drift from the truth: which host actually answered.
            That matters on a console reached through tunnels. Deliberately NOT a hardcoded
            environment name — an internal deployment identifier is not operator-facing copy (R5). */}
        <span className="hero__where">{window.location.host}</span>
      </header>

      <div className="hero__stage">
        <div className="hero__copy">
          <p className="hero__eyebrow hero__reveal" style={{ '--hero-step': 0 } as CSSProperties}>
            Operator access
          </p>
          <h1 className="hero__title hero__reveal" style={{ '--hero-step': 1 } as CSSProperties}>
            One operator.
            <br />
            One key.
          </h1>
          <p className="hero__lede hero__reveal" style={{ '--hero-step': 2 } as CSSProperties}>
            This console is opened by a single enrolled passkey. There is no password, no second
            credential, and no way to enrol one from this screen.
          </p>
          <div className="hero__actions hero__reveal" style={{ '--hero-step': 3 } as CSSProperties}>
            {action}
          </div>
        </div>
      </div>

      <footer className="hero__rails">
        {RAILS.map((rail) => (
          <p className="hero__rail" key={rail.term}>
            <b>{rail.term}</b>
            {rail.gloss}
          </p>
        ))}
      </footer>
    </main>
  );
}

export function AuthSurface({ onAuthenticated }: { onAuthenticated: (principalId: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ title: string; detail: string } | null>(null);

  const authenticate = async () => {
    setBusy(true);
    setError(null);
    try {
      // 1. request assertion options from the Nest WebAuthn guard (public route).
      const { options } = await apiGet('/auth/webauthn/options', optionsSchema);
      // 2. run the browser passkey ceremony (spec-compliant assertion).
      const assertion = await startAuthentication({ optionsJSON: options as never });
      // 3. verify server-side; on ALLOW the Nest guard mints the signed session cookie.
      const session = await apiPost('/auth/webauthn/verify', { assertion }, sessionSchema);
      setCsrfToken(session.csrfToken);
      onAuthenticated(session.principalId);
    } catch (e) {
      /*
       * A typed refusal and a ceremony that did not complete are different events and are named
       * differently — but the machine-readable reason is reproduced VERBATIM in both cases rather
       * than being smoothed into friendlier prose. This is the one console for a production
       * estate; the operator needs the actual reason, not a reassurance.
       */
      if (e instanceof ApiRefusal) {
        setError({ title: 'Sign-in refused', detail: `${e.reason} (HTTP ${e.status})` });
      } else {
        setError({
          title: 'Sign-in did not complete',
          detail: e instanceof Error ? e.message : 'passkey ceremony failed',
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <HeroFrame
      action={
        <>
          <button className="hero__cta" onClick={authenticate} disabled={busy} aria-busy={busy}>
            {busy ? 'Waiting for your device…' : 'Sign in with passkey'}
            {busy ? (
              <span className="hero__cta-pulse" aria-hidden />
            ) : (
              <span className="hero__cta-arrow" aria-hidden>
                →
              </span>
            )}
          </button>

          {/* Says what is about to happen before it happens — the wait after this click is an OS
              prompt the page cannot observe, so the expectation has to be set here. The live
              region announces the change for screen readers without moving focus off the button. */}
          <p className="hero__hint" role="status" aria-live="polite">
            {busy
              ? 'Confirm on your device to continue. Your key never leaves it.'
              : "Your browser will ask your device to confirm it's you."}
          </p>

          {error && (
            <div className="hero__error" role="alert">
              <strong>{error.title}</strong>
              <code>{error.detail}</code>
            </div>
          )}
        </>
      }
    />
  );
}
